/* eslint-disable react/no-array-index-key */
import React, {Fragment, useCallback, useContext, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faUpload, faShareNodes} from "@fortawesome/free-solid-svg-icons";

// Contexts
import {SettingsContext} from "../../contexts/settings.js";
import {FacilityNavContext} from "../../contexts/facilitynav.js";

// Hooks
import useApi from "../../hooks/useApi.js";

// Components
import Help from "../../components/Help.js";
import {stateLabel} from "../../components/ComponentServiceStateHistory.js";

// Utils
import {GROUP, COMPONENT, FIELD, filterNonexistentField} from "../../utils/builder.js";
import {addCommas, convertToUserTimezone, exists, fromMilitary} from "../../utils/helpers.js";
import {
  evaluateConditional,
  evaluateInvalid,
  evaluateRange,
  getAncestryName,
  getWeatherConditionValue,
  isWeatherConditionDepends
} from "../checksheet-builder/helpers.js";
// Style
import {bp} from "../../style/components/breakpoints.js";
import {flex} from "../../style/components/mixins.js";
import {voice} from "../../style/components/typography.js";
import {border, colors, pad, radius} from "../../style/components/variables.js";
import {
  Title,
  Small,
  Text,
  Inline,
  Error,
  FormGroupHeading,
  Pill,
  Abbr,
  RelativeWrapper
} from "../../style/components/general.js";

const resolveComponentStateDisplay = (stateInTask, stateAtSubmission) => {
  if (stateInTask) {
    return {
      showState: true,
      changed: true,
      prevDisabled: !stateInTask.disabled,
      newDisabled: stateInTask.disabled,
      explanation: stateInTask.explanation,
      userLabel: stateInTask.userLabel
    };
  }

  // No recorded change during this record and no prior state change before it means
  // the component was in its default (In Service) state at the time of submission.
  return {
    showState: true,
    changed: false,
    effectiveDisabled: !!stateAtSubmission?.disabled,
    explanation: null
  };
};

const RenderSubmission = React.forwardRef(
  (
    {
      submission,
      submissionName = "",
      taskType = "checksheet",
      builder,
      notifications,
      weather,
      dateDue,
      completedAt,
      draft = false,
      overdue = false,
      modifications,
      highlight,
      viewModification = false,
      filterResult,
      createdAt,
      updatedAt,
      showOutOfServiceComponents = false
    },
    ref
  ) => {
    const {facility} = useContext(FacilityNavContext);
    const {settings} = useContext(SettingsContext);

    const {api: fileApi} = useApi("files");

    const [fileDict, setFileDict] = useState(null);

    const links = useMemo(() => {
      if (fileDict) return fileDict.links;
      return null;
    }, [fileDict]);

    const files = useMemo(() => {
      if (fileDict) return fileDict.files;
      return null;
    }, [fileDict]);

    const {allIds, byId} = useMemo(() => {
      const {allIds: taskAllIds, byId: taskById} = builder;
      const {byId: facilityById} = facility?.builder || {};

      if (taskType === "event") return {allIds: taskAllIds, byId: taskById};

      const consolidated = {allIds: taskAllIds, byId: {}};
      Object.values(taskById).map(attributes => {
        const {name, help, children, condition, deleted} = attributes;
        // Consolidate task/facility attributes for groups/components
        if (facility && name in facilityById) {
          const taskHelp = help || [];
          const facilityHelp = facilityById[name]?.help || [];
          const facilityDeleted = facilityById[name]?.deleted;
          const facilityCondition = facilityById[name]?.condition;
          const consolidatedHelp = [...facilityHelp, ...taskHelp];
          const consolidatedElement = {
            ...facilityById[name],
            condition: facilityCondition || condition,
            children,
            deleted: facilityDeleted || deleted
          };

          if (consolidatedHelp?.length > 0) consolidatedElement.help = consolidatedHelp;

          consolidated.byId[name] = consolidatedElement;
        } else consolidated.byId[name] = attributes;
      });

      return consolidated;
    }, [facility, builder, taskType]);

    const dateForConditional = useMemo(() => {
      if (overdue) return convertToUserTimezone(dateDue, settings.timezone);
      return convertToUserTimezone(completedAt, settings.timezone);
    }, [overdue, dateDue, settings, completedAt]);

    const compareStates = (state1, state2) => {
      if (!state1 && !state2) return undefined;
      if (!state1) return state2;
      if (!state2) return state1;
      if (state1.disabled === state2.disabled) return state1;
      return state1.time >= state2.time ? state1 : state2;
    };

    const getComponentStateAtSubmission = useCallback(
      attributes => {
        const {stateHistory} = attributes;
        if (!stateHistory?.length) return undefined;

        const dateToUse = draft ? updatedAt || createdAt : createdAt;
        const recordEpoch = new Date(dateToUse).getTime();

        // check earliest state change to avoid iterating all the way through history every time
        const earliest = stateHistory[stateHistory.length - 1];
        // if earliest state change occurred after the record, we assume the component was not disabled
        if (earliest.time > recordEpoch) return undefined;

        // iterate over stateHistory, stop when time in history entry is less than record epoch
        let i = 0;
        while (i < stateHistory.length && stateHistory[i].time >= recordEpoch) i++;

        // 'i' will be the index corresponding to the most recent state change at the time
        // of record completion
        return i === stateHistory.length ? undefined : stateHistory[i];
      },
      [createdAt, draft, updatedAt]
    );

    const getDisabledState = useCallback(
      (attributes, stateInTask) => {
        const stateToUse = compareStates(stateInTask, getComponentStateAtSubmission(attributes));

        return stateToUse?.disabled || false;
      },
      [getComponentStateAtSubmission]
    );

    const shouldShowConditional = (condition, {treatParentAsEnabled = false} = {}) => {
      if (!condition || !condition.action || !condition.list || condition.list.length === 0)
        return true;

      const hideOnTrue = condition.action.includes("Hide");
      const showOnTrue = condition.action.includes("Show");

      if (!hideOnTrue && !showOnTrue) return true;

      let check = true;
      const results = [];
      const alwaysShow = !condition.action.includes("Show") && !condition.action.includes("Hide");
      const hideWhenTrue = condition.action.includes("Hide");
      const showWhenTrue = condition.action.includes("Show");

      for (let i = 0; i < condition.list.length; i++) {
        //  Field Comparisons
        // "parentDisabled" is a Component Service State condition trigger (parent component in/out of service)
        if (condition.list[i].trigger === "parentDisabled") {
          if (!byId[condition.list[i].depends]) check = false;
          else {
            const stateInTask = submission?.responses?.[`${condition.list[i].depends}_state`];
            let disabled = treatParentAsEnabled
              ? false
              : getDisabledState(byId[condition.list[i].depends], stateInTask);

            // just in case disabled isn't properly set, fallback to false
            disabled = disabled ?? false;

            check = evaluateConditional(
              disabled ? "disabled" : "enabled",
              condition.list[i].check,
              condition.list[i].compare,
              byId[condition.list[i].depends],
              condition.list[i].operator
            );
          }
        } else if (
          !["weekday", "date"].includes(condition.list[i].depends) &&
          !isWeatherConditionDepends(condition.list[i].depends)
        )
          check = evaluateConditional(
            submission?.responses?.[condition.list[i].depends],
            condition.list[i].check,
            condition.list[i].compare,
            byId[condition.list[i].depends],
            condition.list[i].operator
          );
        else if (isWeatherConditionDepends(condition.list[i].depends)) {
          check = evaluateConditional(
            getWeatherConditionValue({weather}, condition.list[i].depends),
            condition.list[i].check,
            condition.list[i].compare,
            {type: "number"},
            condition.list[i].operator
          );
        } else {
          check = evaluateConditional(
            dateForConditional,
            condition.list[i].check,
            condition.list[i].compare,
            condition.list[i].depends,
            condition.list[i].operator
          );
        }

        if (check && condition?.operator === "or") break;

        results.push(check);
      }

      if (condition?.operator === "and") check = results.every(r => !!r);

      return (check && showWhenTrue) || (!check && hideWhenTrue) || alwaysShow;
    };

    useEffect(() => {
      let fileIds = Object.keys(submission.responses)
        .filter(
          key =>
            byId[key] &&
            byId[key].type === "upload" &&
            exists(submission.responses[key]) &&
            submission.responses[key] !== "_CONDITION_UNSATISFIED"
        )
        .flatMap(key => `${submission.responses[key]}`.split(","));

      if (modifications)
        fileIds = [
          ...fileIds,
          ...Object.keys(modifications)
            .filter(
              key =>
                byId[key] &&
                byId[key].type === "upload" &&
                exists(modifications[key]) &&
                modifications[key] !== "_CONDITION_UNSATISFIED"
            )
            .map(key => `${modifications[key]}`.split(","))
        ];

      if (fileIds?.length > 0)
        fileApi.callGet("", {ids: fileIds, getSerialized: true}).then(({status, data}) => {
          if (status === 200) setFileDict(data || {});
        });
      else setFileDict({});
    }, [byId, fileApi, modifications, submission]);

    const formatDiff = (
      name,
      value,
      oldValue,
      qualifier = null,
      oldQualifier = null,
      type = null,
      units = null,
      precision = null,
      degree = null,
      hideDiff = false
    ) => {
      const newResponse = oldValue === "" || oldValue === "_CONDITION_UNSATISFIED";

      const inMinutes = byId[name]?.minutes;

      // Don’t render a diff if value and qualifier are both unchanged
      let valueUnchanged = value === oldValue;
      const qualifierUnchanged = qualifier === oldQualifier || (!qualifier && !oldQualifier);

      let coalescedValue = oldValue;
      let coalescedQualifier = oldQualifier;

      // If only one of qualifier or value changed, use current version of the other
      if (coalescedQualifier || coalescedValue) {
        if (!coalescedValue) coalescedValue = value;
        if (!coalescedQualifier) coalescedQualifier = qualifier;
      }

      // Special handling for uploads
      if (type === "upload" && files && links) {
        const oldFileIds = oldValue?.split(",");
        const newFileIds = value?.split(",");
        const oldFiles = oldFileIds?.map(id => links[id]);
        const newFiles = newFileIds?.map(id => files[id]);

        const oldFileLinks = oldFileIds?.map(id => links[id]);
        const newFileLinks = newFileIds?.map(id => links[id]);

        if (
          (!oldFileIds && !newFileIds) ||
          (oldFileIds &&
            newFileIds &&
            oldFileIds.every(id => newFileIds.includes(id)) &&
            oldFileIds.length === newFileIds.length)
        )
          valueUnchanged = true;

        coalescedValue = oldFiles?.map((file, i) => (
          <a key={`${name}-${i}-old`} href={oldFileLinks[i]} target="_blank" rel="noreferrer">
            {file?.label || "Uploaded File"}
          </a>
        ));

        value = newFiles?.map((file, i) => (
          <a key={`${name}-${i}-new`} href={newFileLinks[i]} target="_blank" rel="noreferrer">
            {file?.label || "Uploaded File"}
          </a>
        ));
      } else if (type === "upload") {
        return (
          <StyledSpan data-testid="response.newVal" small>
            Loading files...
          </StyledSpan>
        );
      }

      // Handle number formatting
      if (["number", "generated", "range"].includes(type)) {
        const precisionOverride =
          degree === "Decimal" || type === "generated" ? precision ?? 2 : undefined;

        coalescedValue = addCommas(coalescedValue, precisionOverride, inMinutes ? 60 : null);
        value = addCommas(value, precisionOverride, inMinutes ? 60 : null);

        if (value === coalescedValue) {
          valueUnchanged = true;
        }

        if (units) {
          const unitsToUse = inMinutes ? "minutes" : units;
          if (exists(coalescedValue)) coalescedValue += ` ${unitsToUse}`;
          if (exists(value)) value += ` ${unitsToUse}`;
        }
      }

      if (valueUnchanged && qualifierUnchanged) {
        return (
          <StyledSpan data-testid="response.newVal">{value || "NO RESPONSE PROVIDED"}</StyledSpan>
        );
      }

      return (
        <FlexSpan>
          {!hideDiff &&
            viewModification &&
            (coalescedValue || coalescedQualifier || newResponse) && (
              <>
                <Strikethrough data-testid="response.oldVal" isFile={type === "upload"}>
                  {!newResponse &&
                  coalescedQualifier &&
                  !["DNQ", "ND", "EST"].includes(coalescedQualifier)
                    ? `${coalescedQualifier} `
                    : ""}
                  {!newResponse && coalescedQualifier === "ND" ? "< " : ""}
                  {!newResponse ? coalescedValue : "NO RESPONSE PROVIDED"}
                  {!newResponse && ["DNQ", "ND", "EST"].includes(coalescedQualifier)
                    ? ` (${coalescedQualifier})`
                    : ""}
                </Strikethrough>{" "}
              </>
            )}
          <StyledSpan data-testid="response.newVal" isFile={type === "upload"}>
            {qualifier && !["DNQ", "ND", "EST"].includes(qualifier) ? `${qualifier} ` : ""}
            {qualifier === "ND" ? "< " : ""}
            {value || "NO RESPONSE PROVIDED"}
            {["DNQ", "ND", "EST"].includes(qualifier) ? ` (${qualifier})` : ""}
          </StyledSpan>
        </FlexSpan>
      );
    };

    const getOutOfRangeComment = name => {
      if (
        submission.responses[`${name}_comment`] &&
        submission.responses[`${name}_comment`] !== "_NEEDS_COMMENT"
      )
        return submission.responses[`${name}_comment`];
      if (submission.responses[name] && !draft && !overdue)
        return "Field was submitted with an unacceptable parameter";
      return "";
    };

    const hiddenOnlyByParentServiceState = (elementObj, ancestorOutOfService) => {
      if (!showOutOfServiceComponents || !ancestorOutOfService || !elementObj) return false;
      if (submission.responses[elementObj.name] !== "_CONDITION_UNSATISFIED") return false;
      if (shouldShowConditional(elementObj.condition)) return false;
      return shouldShowConditional(elementObj.condition, {treatParentAsEnabled: true});
    };

    const renderFields = (ids, ancestorDeleted = false, ancestorOutOfService = false) => {
      const rendered = ids
        ?.filter(id => {
          const elementObj = byId[id];
          if (!elementObj || elementObj.hidden) return false;
          if (!filterNonexistentField(id, byId, submission.responses, true)) return false;

          const revealOutOfService = hiddenOnlyByParentServiceState(
            elementObj,
            ancestorOutOfService
          );
          if (submission.responses[id] === "_CONDITION_UNSATISFIED" && !revealOutOfService)
            return false;
          return shouldShowConditional(elementObj.condition) || revealOutOfService;
        })
        ?.map(id => {
          const elementObj = byId[id];
          const {
            label,
            name,
            tag,
            element,
            hasAlert,
            hasARange,
            aMin,
            aMax,
            hasSetPoint,
            setLow,
            setHigh,
            type,
            hasQualifier,
            children,
            military,
            units,
            alertCondition,
            degree,
            precision,
            shared,
            stale,
            deleted,
            allowDisable,
            enabledLabel,
            disabledLabel
          } = elementObj;

          let precisionNumber = precision;
          if (exists(precisionNumber) && typeof precisionNumber === "string")
            precisionNumber = parseInt(precisionNumber, 10);

          let value = submission.responses[id];
          let oldValue;
          let hideDiff = false;

          const hasExplanation =
            exists(value) ||
            (modifications &&
              `${name}_comment` in modifications &&
              modifications[`${name}_comment`] !== "_NEEDS_COMMENT");

          if (modifications && id in modifications) oldValue = modifications[id];

          if (!value || value === "NO RESPONSE PROVIDED" || value === "_CONDITION_UNSATISFIED")
            value = value === null || value === undefined ? value : "";

          if (oldValue === "_CONDITION_UNSATISFIED" || oldValue === "NO RESPONSE PROVIDED")
            hideDiff = true;

          if (type === "time") {
            value = military ? value : fromMilitary(value);
            oldValue = military || !oldValue ? oldValue : fromMilitary(oldValue);
          }

          if (type === "generated") {
            value = value === "_NO_PREVIOUS" || value === "_DEP_IS_INF" ? "" : value;
            oldValue = oldValue === "_NO_PREVIOUS" || value === "_DEP_IS_INF" ? "" : oldValue;
          }

          let qualifier = null;
          let oldQualifier = null;

          if (hasQualifier && submission.responses[`${name}_qualifierEnabled`])
            qualifier = submission.responses[`${name}_qualifier`];

          if (
            hasQualifier &&
            modifications &&
            (`${name}_qualifierEnabled` in modifications || modifications[`${name}_qualifier`])
          )
            oldQualifier = modifications[`${name}_qualifier`];

          const componentOutOfService =
            element === COMPONENT &&
            getDisabledState(elementObj, submission?.responses?.[`${name}_state`]);

          const renderedChildren =
            children?.length > 0
              ? renderFields(
                  children,
                  deleted || ancestorDeleted,
                  ancestorOutOfService || componentOutOfService
                )
              : [];
          if (
            element !== FIELD &&
            !(showOutOfServiceComponents && componentOutOfService) &&
            // only show element when it has visible children
            (!renderedChildren ||
              renderedChildren.length === 0 ||
              renderedChildren.every(child => !child))
          )
            return null;

          if (element === GROUP)
            return (
              <Group key={name}>
                <Inline>
                  <StyledGroupHeading deleted={deleted}>{label.toUpperCase()}</StyledGroupHeading>
                  {deleted && (
                    <HelpWrapper>
                      <Help>Group has been deleted since this response was recorded</Help>
                    </HelpWrapper>
                  )}
                </Inline>
                <InnerWrapper deleted={deleted}>{renderedChildren}</InnerWrapper>
              </Group>
            );

          if (element === COMPONENT) {
            const stateInTask = submission?.responses?.[`${name}_state`];
            const stateAtSubmission = getComponentStateAtSubmission(elementObj);
            const stateDisplay = resolveComponentStateDisplay(stateInTask, stateAtSubmission);

            return (
              <Component key={name} data-testid={`component-${name}`}>
                <Inline>
                  <StyledComponentHeading deleted={!ancestorDeleted && deleted}>
                    {label.toUpperCase()}
                  </StyledComponentHeading>
                  {allowDisable && stateDisplay.showState && (
                    <ComponentStateTransition>
                      {stateDisplay.changed ? (
                        <>
                          <ComponentStateText disabled={stateDisplay.prevDisabled}>
                            {stateLabel(stateDisplay.prevDisabled, enabledLabel, disabledLabel)}
                          </ComponentStateText>
                          <ComponentStateArrow>→</ComponentStateArrow>
                          <ComponentStateText disabled={stateDisplay.newDisabled}>
                            {stateLabel(stateDisplay.newDisabled, enabledLabel, disabledLabel)}
                          </ComponentStateText>
                        </>
                      ) : (
                        <ComponentStateText disabled={stateDisplay.effectiveDisabled}>
                          {stateLabel(stateDisplay.effectiveDisabled, enabledLabel, disabledLabel)}
                        </ComponentStateText>
                      )}
                    </ComponentStateTransition>
                  )}
                  {!ancestorDeleted && deleted && (
                    <HelpWrapper>
                      <Help>Component has been deleted since this response was recorded</Help>
                    </HelpWrapper>
                  )}
                </Inline>
                {allowDisable && stateDisplay.changed && stateDisplay.explanation && (
                  <>
                    <ComponentStateExplanation quiet small>
                      <strong>Set By:</strong> {stateDisplay.userLabel}
                    </ComponentStateExplanation>
                    <ComponentStateExplanation quiet small>
                      <strong>Reason:</strong> {stateDisplay.explanation}
                    </ComponentStateExplanation>
                    <br />
                  </>
                )}
                <InnerWrapper deleted={!ancestorDeleted && deleted}>
                  {renderedChildren}
                </InnerWrapper>
              </Component>
            );
          }

          if (element === FIELD && (!filterResult || filterResult[id])) {
            let invalidCategorical = false;
            let invalidNumerical = false;
            if (exists(value) && hasAlert && exists(alertCondition))
              invalidCategorical = evaluateInvalid(value, alertCondition);
            if (exists(value) && hasARange && (exists(aMin) || exists(aMax)))
              invalidNumerical = evaluateRange(value.replace(/,/g, ""), elementObj, qualifier);

            const fieldOutOfService = hiddenOnlyByParentServiceState(
              elementObj,
              ancestorOutOfService
            );

            return (
              <RelativeWrapper key={name}>
                <Field
                  data-testid={`field-${name}`}
                  $outOfService={fieldOutOfService ? 1 : 0}>
                  <Inline>
                    <Icon>{type === "upload" && <FontAwesomeIcon icon={faUpload} />}</Icon>

                    <LabelWrapper>
                      <Inline>
                        <Key data-testid={name}>
                          <InnerLabelWrapper stale={stale}>
                            {stale ? "(OUTDATED) " : ""}
                            {label.toUpperCase()}
                          </InnerLabelWrapper>
                          &nbsp;&nbsp;
                          {stale && (
                            <HelpWrapper>
                              <Help>
                                {byId[stale]?.type === type
                                  ? "Field was changed to shared"
                                  : "Field type has changed"}{" "}
                                since this response was recorded
                              </Help>
                            </HelpWrapper>
                          )}
                          &nbsp;
                          {shared && (
                            <HelpWrapper>
                              <Help icon={<FontAwesomeIcon icon={faShareNodes} />}>
                                Field is shared accross checksheets.
                              </Help>
                            </HelpWrapper>
                          )}
                        </Key>
                        {tag && (
                          <TagPill stale={stale}>
                            <Abbr title={tag}>{tag}</Abbr>
                          </TagPill>
                        )}
                      </Inline>
                    </LabelWrapper>
                    <Value highlight={highlight && highlight === name} stale={stale}>
                      {exists(value) || (exists(oldValue) && viewModification)
                        ? formatDiff(
                            name,
                            value,
                            oldValue,
                            qualifier,
                            oldQualifier,
                            type,
                            units,
                            precisionNumber,
                            degree,
                            hideDiff
                          )
                        : "NO RESPONSE PROVIDED"}
                      <Small>{hasSetPoint && `(Set point: ${setLow} - ${setHigh})`}</Small>
                      {highlight && highlight === name && <Small>&nbsp;(Target Value)</Small>}
                    </Value>
                  </Inline>
                  {/* Messages */}
                  {invalidNumerical && (
                    <Exception stale={stale}>
                      {exists(aMin) && !exists(aMax)
                        ? `Unacceptable value reported. Must be greater than ${aMin} ${units}.`
                        : ""}
                      {exists(aMax) && !exists(aMin)
                        ? `Unacceptable value reported. Must be less than ${aMax} ${units}.`
                        : ""}
                      {exists(aMin) && exists(aMax)
                        ? `Unacceptable value reported. Must be within range ${aMin} - ${aMax} ${units}.`
                        : ""}
                    </Exception>
                  )}
                  {invalidCategorical && Array.isArray(alertCondition) && (
                    <Exception stale={stale}>
                      Unacceptable value reported. Unacceptable Values: {alertCondition.join(", ")}
                    </Exception>
                  )}
                  {invalidCategorical && !Array.isArray(alertCondition) && (
                    <Exception stale={stale}>
                      Unacceptable value reported. Unacceptable Value: {alertCondition}
                    </Exception>
                  )}
                </Field>

                {invalidNumerical && hasExplanation && (
                  <Field stale={stale}>
                    <Inline>
                      <Explanation data-testid={`${name}_comment`}>
                        Provided&nbsp;Explanation:
                      </Explanation>

                      <Value quiet>
                        {modifications &&
                        `${name}_comment` in modifications &&
                        modifications[`${name}_comment`] !== "_NEEDS_COMMENT"
                          ? formatDiff(
                              name,
                              submission.responses[`${name}_comment`] === "_NEEDS_COMMENT"
                                ? ""
                                : submission.responses[`${name}_comment`],
                              modifications[`${name}_comment`] === "_NEEDS_COMMENT"
                                ? ""
                                : modifications[`${name}_comment`]
                            )
                          : getOutOfRangeComment(name)}
                      </Value>
                    </Inline>
                  </Field>
                )}

                {invalidCategorical && hasExplanation && (
                  <Field stale={stale}>
                    <Inline>
                      <Explanation data-testid={`${name}_comment`}>
                        Provided&nbsp;Explanation:
                      </Explanation>

                      <Value quiet>
                        {modifications &&
                        `${name}_comment` in modifications &&
                        modifications[`${name}_comment`] !== "_NEEDS_COMMENT"
                          ? formatDiff(
                              name,
                              submission.responses[`${name}_comment`] === "_NEEDS_COMMENT"
                                ? ""
                                : submission.responses[`${name}_comment`],
                              modifications[`${name}_comment`] === "_NEEDS_COMMENT"
                                ? ""
                                : modifications[`${name}_comment`]
                            )
                          : getOutOfRangeComment(name)}
                      </Value>
                    </Inline>
                  </Field>
                )}

                {value &&
                  type === "weather" &&
                  (submission.responses[`${name}_rainfall`] ||
                    (modifications && modifications[submission.responses[`${name}_rainfall`]])) && (
                    <Field stale={stale}>
                      <Key data-testid={`${name}_rainfall`}>
                        Previous Day&apos;s Cumulative Rainfall:
                      </Key>

                      {modifications && `${name}_rainfall` in modifications
                        ? formatDiff(
                            name,
                            submission.responses[`${name}_rainfall`],
                            modifications[`${name}_rainfall`]
                          )
                        : submission.responses[`${name}_rainfall`]}
                    </Field>
                  )}
              </RelativeWrapper>
            );
          }

          return null;
        });

      let hasAddedNotifications = false;
      if (byId && notifications?.byId)
        Object.keys(notifications.byId).map((key, i) => {
          if (submission.responses[`${key}_comment`]) {
            if (!hasAddedNotifications) rendered.push(<hr key="notificationDivider" />);
            hasAddedNotifications = true;

            const notification = notifications.byId[key];
            const nElKey = `notification${i}`;

            rendered.push(
              <Fragment key={nElKey}>
                <Field>
                  <Key>{notification.name?.toUpperCase() || "MULTI-FIELD PARAMETER"}</Key>
                  {notification?.conditionArray
                    ?.filter(({depends: condDepends}) => byId[condDepends])
                    ?.map(({depends: condDepends, aMin, aMax, alertCondition}, nIdx) => {
                      const {units} = byId[condDepends];
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={`notification-${condDepends}-${nIdx}`}>
                          {exists(aMin) && exists(aMax) && (
                            <Exception>
                              <strong>{getAncestryName(byId[condDepends], {byId})}:</strong>{" "}
                              Unacceptable value reported. Must be within range {aMin} - {aMax}{" "}
                              {units}
                            </Exception>
                          )}
                          {exists(aMin) && !exists(aMax) && (
                            <Exception>
                              <strong>{getAncestryName(byId[condDepends], {byId})}:</strong>{" "}
                              Unacceptable value reported. Must be greater than {aMin} {units}
                            </Exception>
                          )}
                          {!exists(aMin) && exists(aMax) && (
                            <Exception>
                              <strong>{getAncestryName(byId[condDepends], {byId})}:</strong>{" "}
                              Unacceptable value reported. Must be less than {aMin} {units}
                            </Exception>
                          )}
                          {Array.isArray(alertCondition) && (
                            <Exception>
                              <strong>{getAncestryName(byId[condDepends], {byId})}:</strong>{" "}
                              Unacceptable value reported. Unacceptable Values:{" "}
                              {alertCondition.join(", ")}
                            </Exception>
                          )}
                          {typeof alertCondition === "string" && (
                            <Exception>
                              <strong>{getAncestryName(byId[condDepends], {byId})}:</strong>{" "}
                              Unacceptable value reported. Unacceptable Value: {alertCondition}
                            </Exception>
                          )}
                        </div>
                      );
                    })}
                </Field>
                <Field>
                  <Inline>
                    <Explanation>Provided&nbsp;Explanation:</Explanation>

                    <Value quiet>
                      {modifications && `${key}_comment` in modifications
                        ? formatDiff(
                            key,
                            submission.responses[`${key}_comment`],
                            modifications[`${key}_comment`]
                          )
                        : getOutOfRangeComment(key)}
                    </Value>
                  </Inline>
                </Field>
              </Fragment>
            );
          }
        });

      return rendered;
    };

    const fields = renderFields(allIds);

    return (
      <div ref={ref}>
        <Inline>
          <Title>{submissionName}</Title>
          {submission?.frequency && <Frequency>{submission.frequency.toUpperCase()}</Frequency>}
          {draft && <Draft>INCOMPLETE</Draft>}
        </Inline>
        {fields?.length > 0 && !fields?.every(item => !item) ? (
          fields
        ) : (
          <Message>No fields found for {filterResult ? "these filters" : "this record"}</Message>
        )}
      </div>
    );
  }
);

RenderSubmission.displayName = "Checksheet Submission";

RenderSubmission.propTypes = {
  submission: PropTypes.objectOf(PropTypes.any).isRequired,
  taskType: PropTypes.string,
  builder: PropTypes.objectOf(PropTypes.any).isRequired,
  notifications: PropTypes.objectOf(PropTypes.any),
  weather: PropTypes.objectOf(PropTypes.any),
  dateDue: PropTypes.string,
  completedAt: PropTypes.string,
  draft: PropTypes.bool,
  overdue: PropTypes.bool,
  modifications: PropTypes.objectOf(PropTypes.any),
  highlight: PropTypes.string,
  viewModification: PropTypes.bool,
  submissionName: PropTypes.string,
  filterResult: PropTypes.objectOf(PropTypes.any),
  createdAt: PropTypes.string,
  updatedAt: PropTypes.string,
  showOutOfServiceComponents: PropTypes.bool
};

// Style Overrides
const Field = styled.div`
  display: block;

  ${({stale}) =>
    stale &&
    css`
      opacity: 0.6;
    `}

  ${({$outOfService}) =>
    $outOfService &&
    css`
      opacity: 0.5;
    `}

  ${({spaced}) =>
    spaced &&
    css`
      margin: ${pad / 2}px 0;
    `}
`;

const Key = styled(Text)`
  color: ${({theme}) => theme.field};
  padding: ${pad}px ${pad}px 0;
  font-weight: bold;
  margin-top: -${pad}px;

  ${({stale}) =>
    stale &&
    css`
      opacity: 0.6;
    `}

  ${bp(3)} {
    padding: ${pad}px;
  }
`;

const Explanation = styled(Text)`
  ${voice.quiet}

  color: ${({theme}) => theme.secondary};
  padding: ${pad}px ${pad}px 0;
  font-weight: bold;
  margin-top: -${pad}px;
  white-space: nowrap;

  ${({stale}) =>
    stale &&
    css`
      opacity: 0.6;
    `}

  ${bp(3)} {
    padding: ${pad}px;
  }
`;

const Value = styled.span`
  display: block;
  color: ${({theme}) => theme.secondary};
  padding: ${pad * 1.5}px ${pad}px 0;
  margin: -${pad}px 0 ${pad / 2}px;

  ${({stale}) =>
    stale &&
    css`
      opacity: 0.6;
    `}

  ${bp(3)} {
    display: inline;
    padding: 0 ${pad}px ${pad}px;
    margin: 0;
  }

  @media print {
    color: ${colors.heroBlack};
  }

  ${({quiet}) =>
    quiet &&
    css`
      ${voice.quiet}
    `}

  ${({highlight, theme}) =>
    highlight &&
    css`
      color: ${theme.highlight};
    `}
`;

const Exception = styled(Error)`
  ${voice.quiet};
  padding: 0 ${pad}px ${pad}px;

  ${({stale}) =>
    stale &&
    css`
      opacity: 0.6;
    `}
`;

const Component = styled.div`
  padding: ${pad}px;
  border-radius: ${radius};

  &:last-child {
    margin-bottom: 0;
  }
`;

const Group = styled.div`
  padding: ${pad}px;
  margin: ${pad}px 0;
  border: ${border} solid ${({theme}) => theme.group};
  border-radius: ${radius};

  @media print {
    display: block;
    page-break-after: auto;
    page-break-inside: avoid;
  }
`;

const StyledComponentHeading = styled(FormGroupHeading)`
  color: ${({theme}) => theme.component};

  ${({deleted}) =>
    deleted &&
    css`
      opacity: 0.6;
    `}
`;

const ComponentStateTransition = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${pad / 4}px;
`;

const ComponentStateText = styled.span`
  font-weight: bold;
  color: ${({theme, disabled}) => (disabled ? theme.error : theme.success)};
`;

const ComponentStateArrow = styled.span`
  font-weight: bold;
  color: ${({theme}) => theme.secondary};
`;

const ComponentStateExplanation = styled(Text)`
  padding: 0 ${pad}px ${pad / 2}px;
`;

const StyledGroupHeading = styled(FormGroupHeading)`
  color: ${({theme}) => theme.group};
  ${voice.strong};

  ${({deleted}) =>
    deleted &&
    css`
      opacity: 0.6;
    `}
`;

const Draft = styled.span`
  ${voice.quiet}
  text-transform: uppercase;
  font-weight: bold;
  border: ${border} solid ${({theme}) => theme.warning};
  padding: ${pad / 10}px ${pad / 4}px;
  border-radius: 10px;
  background: ${({theme}) => theme.warning};
  color: ${({theme}) => theme.tertiary};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  margin-left: ${pad}px;
`;

const Strikethrough = styled.del`
  color: ${({theme}) => theme.error};
  margin-right: ${pad}px;

  ${({isFile}) =>
    isFile &&
    css`
      margin: ${pad / 4}px;
      a {
        text-decoration: line-through underline;
      }
    `}
`;

const StyledSpan = styled.span`
  ${({small}) =>
    small &&
    css`
      ${voice.small}
    `}

  ${({newVal}) =>
    newVal &&
    css`
      color: ${colors.green};
    `}

  ${({isFile, newVal, theme}) =>
    isFile &&
    css`
      color: ${newVal ? colors.green : theme.secondary};
      margin: ${pad / 4}px;
      a {
        text-decoration: underline;
      }
    `}
`;

const FlexSpan = styled.span`
  ${flex("row", "wrap")}
`;

const LabelWrapper = styled(Inline)`
  gap: ${pad / 2}px;
`;

const Frequency = styled(Pill)`
  min-width: min-content;
`;

const TagPill = styled(Pill)`
  margin-top: -${pad}px;
  margin-left: -${pad / 2}px;
`;

const Message = styled(Text)`
  color: ${({theme}) => theme.secondary};
  margin-top: ${pad * 2}px;
  ${voice.weak};
`;

const HelpWrapper = styled.span`
  position: relative;
`;

const InnerLabelWrapper = styled.span`
  ${({stale, deleted}) =>
    (stale || deleted) &&
    css`
      opacity: 0.6;
    `}
`;

const InnerWrapper = styled.div`
  ${({deleted}) =>
    deleted &&
    css`
      opacity: 0.6;
    `}
`;

const Icon = styled.div`
  margin-top: -10px;
  margin-right: -10px;

  svg {
    fill: ${({theme}) => theme.field};
  }
`;

export default RenderSubmission;
